import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { useParams } from "react-router-dom";
import { db } from "../firebase/config";
import { useJournalFeed } from "../hooks/useJournalFeed";
import JournalFeed from "../components/journal/JournalFeed";

/**
 * Standalone read-only journal page — no authentication required.
 * Resolves publicJournalLinks/{token} → childId, then renders the feed.
 */
const PublicJournalPage = () => {
  const { token } = useParams();
  const [childId,   setChildId]   = useState(null);
  const [childName, setChildName] = useState("");
  const [invalid,   setInvalid]   = useState(false);
  const [resolving, setResolving] = useState(true);

  useEffect(() => {
    if (!token) { setInvalid(true); setResolving(false); return; }
    getDoc(doc(db, "publicJournalLinks", token))
      .then((snap) => {
        if (snap.exists()) {
          setChildId(snap.data().childId);
          setChildName(snap.data().childName || "");
        } else {
          setInvalid(true);
        }
      })
      .catch(() => setInvalid(true))
      .finally(() => setResolving(false));
  }, [token]);

  const { events, loading } = useJournalFeed(childId);

  if (resolving) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  if (invalid) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: "linear-gradient(135deg, #FAFAFA 0%, #F5F5FF 100%)",
        padding: "2rem", textAlign: "center",
      }}>
        <span style={{ fontSize: "3rem", marginBottom: "1rem" }}>🔒</span>
        <h2 style={{ color: "var(--text-dark)", marginBottom: "0.5rem" }}>Link not found</h2>
        <p style={{ color: "var(--text-light)" }}>
          This journal link may have been disabled or regenerated by the owner.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #FAFAFA 0%, #F5F5FF 100%)",
    }}>
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, var(--primary-soft, #FFB3D9) 0%, var(--secondary-soft, #B3E5FC) 100%)",
        padding: "1.25rem 1.5rem",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}>
        <div style={{ maxWidth: "680px", margin: "0 auto", display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <span style={{ fontSize: "1.5rem" }}>📖</span>
          <div>
            <h1 style={{ margin: 0, fontSize: "1.3rem", fontWeight: 700, color: "white" }}>
              {childName ? `${childName}'s Journal` : "Baby Journal"}
            </h1>
            <p style={{ margin: 0, fontSize: "0.8rem", color: "rgba(255,255,255,0.85)" }}>
              Shared with you — read only
            </p>
          </div>
        </div>
      </div>

      {/* Feed */}
      <div style={{ maxWidth: "680px", margin: "0 auto", padding: "1.5rem 1rem 4rem" }}>
        <JournalFeed
          events={events}
          loading={loading}
          emptyMessage="No journal entries have been added yet."
        />
      </div>
    </div>
  );
};

/**
 * Read-only journal view for authenticated email-based viewers.
 * Shown in place of the full dashboard when the selected child is viewer-only.
 */
export const ViewerJournalPage = ({ child }) => {
  const { events, loading } = useJournalFeed(child?.id);

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #FAFAFA 0%, #F5F5FF 100%)",
    }}>
      {/* Banner */}
      <div style={{
        background: "linear-gradient(135deg, rgba(255,179,217,0.15) 0%, rgba(179,229,252,0.15) 100%)",
        borderBottom: "1px solid var(--border-light)",
        padding: "0.75rem 1.5rem",
        textAlign: "center",
        fontSize: "0.82rem",
        color: "var(--text-light)",
      }}>
        👁 You have read-only access to {child?.name ? `${child.name}'s` : "this"} journal
      </div>

      <div style={{ maxWidth: "680px", margin: "0 auto", padding: "1.5rem 1rem 4rem" }}>
        <h2 style={{ color: "var(--primary-dark)", fontWeight: 700, marginBottom: "1.5rem" }}>
          📖 {child?.name ? `${child.name}'s` : ""} Journal
        </h2>
        <JournalFeed
          events={events}
          loading={loading}
          emptyMessage="No journal entries have been added yet."
        />
      </div>
    </div>
  );
};

export default PublicJournalPage;
